import { ReactNode, useCallback, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { motion } from 'motion/react';

type ResizeAfterLoadingDivProps = {
  initialHeight: number;
  isLoading: boolean;
  children: ReactNode;
};

/** Has initial height after loading, then animates height to auto.
 * Remembers height and uses new it for the next loading state.
 * This is use to ensure smooth layout shift when loading dynamic content in vertical layouts */
export const ResizeAfterLoadingDiv = ({ initialHeight, isLoading, children }: ResizeAfterLoadingDivProps) => {
  const [savedHeight, setSavedHeight] = useState(initialHeight);
  const [isAnimating, setIsAnimating] = useState(isLoading);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const handleAnimationComplete = useCallback(() => {
    if (elementRef.current) {
      const height = elementRef.current.offsetHeight;
      setSavedHeight(height);
      setIsAnimating(false);
    }
  }, []);

  return isLoading ? (
    <SavedHeightDiv $height={savedHeight}>{children}</SavedHeightDiv>
  ) : (
    <motion.div
      onAnimationComplete={handleAnimationComplete}
      onAnimationStart={() => setIsAnimating(true)}
      initial={{ height: savedHeight }}
      animate={{ height: 'auto' }}
      transition={{ bounce: 0 }}
      ref={elementRef}
      style={{ overflowY: isAnimating ? 'clip' : 'visible' }}
    >
      {children}
    </motion.div>
  );
};

const SavedHeightDiv = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;

  & > * {
    height: 100%;
  }
`;
